/* 정본 dsl.json(scripts/dsl/build-all.mjs 가 mathcore 소스에서 뽑음)을 읽어 도구 목록과 모델용 스키마를 만든다. 이름과 인자는 여기서 고치지 않는다 */
import DSL_JSON from '../../../data/dsl/dsl.json';
import LABELS_JSON from '../../../data/dsl/labels.json';
import type { DslDocument, DslEntry, DslParam, ToolSchema } from './types';

export const DSL = DSL_JSON as unknown as DslDocument;
export const DSL_ENTRIES: DslEntry[] = DSL.entries;
const LABELS = (LABELS_JSON as unknown as { labels: Record<string, { ko?: string; group?: string; explain?: string }> }).labels;

/** [src:파일:줄] 꼬리표는 사람용이다. 모델 설명에서는 뗀다. 정규식 없이 글자를 훑는다 */
export function stripSources(line: string): string {
  let out = '';
  let i = 0;
  while (i < line.length) {
    if (line.startsWith('[src:', i)) {
      const end = line.indexOf(']', i);
      if (end < 0) break;
      i = end + 1;
      if (out.endsWith(' ')) out = out.slice(0, -1);
      continue;
    }
    out += line[i];
    i += 1;
  }
  return out.split(' ').filter(Boolean).join(' ').trim();
}

/** 값 갈래 → JSON 스키마 조각 */
export function schemaOf(type: string): Record<string, unknown> {
  if (type === 'number') return { type: 'number' };
  if (type === 'boolean') return { type: 'boolean' };
  if (type === 'string') return { type: 'string' };
  if (type === 'number|string') return { type: ['number', 'string'] };
  if (type === 'ref') return { type: 'string', description: '화면 개체의 id' };
  if (type === 'expr') return { type: 'string', description: 'mathcore 식 문법의 식(지시문의 식 문법 절)' };
  if (type === 'color') return { type: 'string', description: '#RRGGBB' };
  if (type === 'point2') return { type: 'array', items: { type: 'number' }, minItems: 2, maxItems: 2 };
  if (type === 'point3') return { type: 'array', items: { type: 'number' }, minItems: 3, maxItems: 3 };
  if (type.startsWith("'")) return { type: 'string', enum: type.split('|').map((one) => one.split("'").join('')) };
  if (type.startsWith('record:')) {
    const [, keyKind, valueType] = type.split(':');
    return { type: 'object', additionalProperties: schemaOf(valueType), description: `키는 ${keyKind === 'ref' ? '개체 id' : keyKind}` };
  }
  if (type.startsWith('{') && type.endsWith('}')) {
    const properties: Record<string, unknown> = {};
    for (const member of type.slice(1, -1).split(';').filter(Boolean)) {
      const at = member.indexOf(':');
      properties[member.slice(0, at)] = schemaOf(member.slice(at + 1));
    }
    return { type: 'object', properties, required: Object.keys(properties) };
  }
  if (type.endsWith('[]')) return { type: 'array', items: schemaOf(type.slice(0, -2)) };
  const open = type.lastIndexOf('[');
  if (open > 0 && type.endsWith(']')) {
    const n = Number(type.slice(open + 1, -1));
    if (Number.isInteger(n)) return { type: 'array', items: schemaOf(type.slice(0, open)), minItems: n, maxItems: n };
  }
  if (type.includes('|')) return { anyOf: type.split('|').map(schemaOf) };
  if (type.startsWith('enum:') || type.startsWith('unknown:')) return { description: `${type.slice(type.indexOf(':') + 1)} 값` };
  return { type: 'string' };
}

/** 정본 usage 줄을 머리로 고른다. 꼬리표는 뗀다 */
function facts(entry: DslEntry, head: string): string[] {
  return (entry.usage ?? []).filter((u) => u.startsWith(head)).map(stripSources).filter(Boolean);
}

/** mathcore 서명 그대로: factory.경로(인자: 형, …) → 만드는 것 · mathcore 함수 이름(사용자 지시 2026-10-15) */
export function signature(entry: DslEntry): string {
  const params = entry.params.map((p) => `${p.name}${p.optional ? '?' : ''}: ${p.type}`).join(', ');
  const made = makes(entry);
  const call =facts(entry, '호출').map((u) => u.slice(u.indexOf('→') + 1).trim()).filter(Boolean);
  const head = entry.backend === 'factory' ? `factory.${(entry.path ?? []).join('.')}` : `command ${entry.command ?? entry.name}`;
  return `${head}(${params})${made ? ` → ${made}` : ''}${call.length ? ` · mathcore ${call.join(', ')}` : ''}`;
}

/** 정본 「만드는 것」 줄의 앞부분: 만들어지는 개체의 종류와 개수 */
function makes(entry: DslEntry): string {
  return facts(entry, '만드는 것').map((u) => u.split(' — ')[0].slice(u.indexOf(':') + 1).trim()).filter(Boolean).join(' | ');
}

/** 도구가 만드는 개체의 종류(정본 「만드는 것」의 앞 낱말). 화면 개체의 type 과 같다 */
export function madeKind(entry: DslEntry): string {
  return makes(entry).split('(')[0].split(' ')[0].trim();
}

/** 정본 「만드는 것」 줄의 「매인 개체 [...]」: 개체가 만들어질 때 서로 관련 개체로 묶이는 인자들. 없거나 비었으면 빈 글자 */
export function relatedOf(entry: DslEntry): string {
  for (const line of facts(entry, '만드는 것')) {
    const at = line.indexOf('매인 개체 [');
    if (at < 0) continue;
    const end = line.indexOf(']', at);
    const list = end < 0 ? '' : line.slice(at + '매인 개체 '.length, end + 1);
    return list === '[]' ? '' : list;
  }
  return '';
}

/** 인자 자리의 보기 값. ref 는 받는 종류의 id(개체는 이름이 아니라 id 로 가리킨다), expr 는 제시된 식 */
function placeholder(p: DslParam, needs: Map<string, string>, accepts: Map<string, string[]>): unknown {
  const t = p.type;
  const kinds = accepts.get(p.name);
  const of = kinds ? kinds.map(kindLabel).join(' 또는 ') : (needs.get(p.name) ?? '개체');
  if (t === 'ref') return `<${of} 의 id>`;
  if (t.startsWith('ref[')) return [`<${of} 의 id>`];
  if (t === 'expr') return '<제시된 식>';
  if (t === 'number' || t === 'number|string') return 0;
  if (t === 'boolean') return false;
  if (t === 'point2') return [0, 0];
  if (t === 'point2[]') return [[0, 0]];
  if (t === 'color') return '#RRGGBB';
  if (t.startsWith("'")) return t.split('|')[0].split("'").join('');
  return `<${t}>`;
}

/** 정본 「앞에 있어야 하는 것」 줄: 인자 이름 → 그 자리에 와야 하는 개체 종류. 이름 뒤의 ref[] 표시는 뗀다 */
export function needsOf(entry: DslEntry): Map<string, string> {
  const needs = new Map<string, string>();
  for (const line of facts(entry, '앞에 있어야 하는 것')) { const body = line.slice(line.indexOf(':') + 1).trim(); const at = body.indexOf(' = '); if (at > 0) needs.set(body.slice(0, at).trim().split(' ')[0], body.slice(at + 3).trim()); }
  return needs;
}

/** 정본 인자의 accepts: 인자 이름 → 받는 개체 type 이름 목록(좌표축 문자열 id 는 axis). 제한이 없으면(accepts 가 any 이거나 없음) 그 인자는 목록에 없다 */
export function acceptsOf(entry: DslEntry): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const p of entry.params) {
    if (!Array.isArray(p.accepts)) continue;
    const kinds = (p.accepts as { type?: unknown }[]).map((a) => a?.type).filter((t): t is string => typeof t === 'string' && t !== '');
    if (kinds.length) out.set(p.name, [...new Set(kinds)]);
  }
  return out;
}

/** 화면 개체 type 이름의 한글 이름. 반려 문장에 쓴다. 목록에 없는 type 은 이름 그대로 쓴다 */
export const KIND_LABELS: Record<string, string> = {
  point: '점', linear: '선(선분, 직선, 반직선 등)', circle: '원(호, 부채꼴 포함)', polygon: '다각형', polynomial: '대수 식의 곡선',
  ellipse: '타원', inequality: '부등식 또는 정적분의 영역', chord: '현', vector: '벡터', bezierCurve: '베지어 곡선', axis: '좌표축', slider: '슬라이더', text: '글',
  plane: '평면 또는 다각형', circular: '원(3D)', sphere: '구', polyhedron: '다면체(각뿔, 각기둥, 정다면체)', cylindroconical: '원뿔 또는 원기둥', algebraic: '식의 곡면',
};
export const kindLabel = (type: string): string => (KIND_LABELS[type] ? `${KIND_LABELS[type]}` : type);

/** 메뉴 줄에 붙이는 짧은 종류 이름 */
const KIND_SHORT: Record<string, string> = { point: '점', linear: '선', circle: '원', polygon: '다각형', polynomial: '곡선', ellipse: '타원', vector: '벡터', inequality: '영역', chord: '현', bezierCurve: '베지어', axis: '좌표축', plane: '면', circular: '원', sphere: '구', polyhedron: '다면체', cylindroconical: '뿔기둥', algebraic: '곡면' };

/** 도구가 받는 개체의 종류를 인자 순서대로: 「점 + 점」, 「원 또는 곡선 또는 타원 + 점 또는 선」. 제한이 없는 인자는 「아무 개체」. 개체를 받지 않으면 빈 글자 */
export function takesText(entry: DslEntry): string {
  const accepts = acceptsOf(entry);
  const parts = entry.params.filter((p) => p.type === 'ref' || p.type.startsWith('ref[')).map((p) => {
    const kinds = accepts.get(p.name);
    const text = kinds ? kinds.map((k) => KIND_SHORT[k] ?? k).join(' 또는 ') : '아무 개체';
    return p.type.startsWith('ref[') ? `${text} 여럿` : text;
  });
  return parts.join(' + ');
}

/** 인자가 받는 종류를 반려 문장용으로: "objectID 는 원(호, 부채꼴 포함), 다각형, 타원" */
export function acceptsText(entry: DslEntry): string {
  const parts = [...acceptsOf(entry)].map(([param, kinds]) => `${param} 는 ${kinds.map(kindLabel).join(', ')}`);
  return parts.length ? `이 도구가 받는 종류: ${parts.join('; ')}` : '';
}

/** 호출 JSON 의 구조 그대로. 모델이 형과 구조를 추론하지 않게 한다(사용자 지시 2026-10-15: 전부 넣는다) */
export function shapeOf(entry: DslEntry): string {
  const needs = needsOf(entry);
  const accepts = acceptsOf(entry);
  const args: Record<string, unknown> = {};
  for (const p of entry.params) args[p.name] = placeholder(p, needs, accepts);
  return JSON.stringify({ tool: entry.name, args });
}

/** 모델에게 주는 설명: 이름, mathcore 서명, 호출 JSON 구조, 앞에 있어야 하는 것, 범위(인자 형식과 값), 거르는 종류, 용도. 전부 싣는다.
 * 다른 갈래, 함께 생기는 이름표, 호출 경로 줄은 형과 구조가 아니라 내부 구현이라 싣지 않는다 */
export function describe(entry: DslEntry): string {
  const accepts = acceptsOf(entry);
  const needs = needsOf(entry);
  // 받는 종류가 정해진 인자는 그 종류를(조사로 바로잡은 것 포함), 아니면 소스에서 뽑은 줄을 싣는다
  const takes = entry.params.filter((p) => accepts.has(p.name) || needs.has(p.name)).map((p) => (accepts.has(p.name) ? `받는 종류: ${p.name} = ${accepts.get(p.name)!.map(kindLabel).join(', ')}` : `앞에 있어야 하는 것: ${p.name} = ${needs.get(p.name)}`));
  return [
    entry.name,
    signature(entry),
    `JSON: ${shapeOf(entry)}`,
    ...takes,
    ...facts(entry, '생성 결과'),
    ...(relatedOf(entry) ? [`매인 개체(관련 개체, RO): ${relatedOf(entry)}. mathcore 는 새 개체와 이 개체들을 서로의 관련 개체로 묶고, 하나가 바뀌면 묶인 개체를 다시 계산한다`] : []),
    ...(entry.range ?? []).map(stripSources).filter(Boolean).map((r) => `범위: ${r}`),
    // 받는 종류가 정해진 도구는 소스에서 뽑은 옛 「본문이 거르는 종류」 줄을 싣지 않는다(실제 동작으로 확인한 받는 종류가 앞선다)
    ...(accepts.size ? [] : facts(entry, '본문이 거르는 종류')),
    ...facts(entry, '용도'),
    ...facts(entry, '참고'),
  ].join('\n');
}

export function toSchema(entry: DslEntry): ToolSchema {
  const properties: Record<string, unknown> = {};
  for (const p of entry.params) properties[p.name] = schemaOf(p.type);
  return {
    type: 'function',
    function: {
      name: entry.name,
      description: describe(entry),
      parameters: { type: 'object', properties, required: entry.params.filter((p) => !p.optional).map((p) => p.name) },
    },
  };
}

/** 취합 단계의 메뉴 한 줄. 도구 UI 의 한글 이름과 설명(labels.json)에 만드는 개체의 종류를 붙인다. 라벨이 없으면 mathcore 서명의 앞부분(만드는 개체 포함) */
export function menuLine(entry: DslEntry): string {
  const label = LABELS[entry.name];
  const takes = takesText(entry);
  const tail = takes ? ` · 받는 것: ${takes}` : '';
  if (label?.ko) return `${entry.name}: ${label.ko}${label.explain ? ` - ${label.explain}` : ''}${makes(entry) ? ` → ${makes(entry)}` : ''}${tail}`;
  return `${entry.name}: ${signature(entry).split(' · mathcore')[0]}${tail}`;
}

/** 메뉴의 묶음 이름: 도구 UI 가 붙인 묶음(labels.json). 라벨이 없으면 빈 글자 */
export function groupTitle(entry: DslEntry): string {
  return LABELS[entry.name]?.group ?? '';
}

export function entriesFor(tool: 'math2d' | 'math3d'): DslEntry[] {
  return DSL_ENTRIES.filter((e) => e.tool === tool);
}

export function entryOf(name: string): DslEntry | undefined {
  return DSL_ENTRIES.find((e) => e.name === name);
}
